/**
 * Utility functions for creating and managing patterns in Konva shapes
 */

/**
 * Creates seamless white diagonal striped pattern for filled annotation blocks
 * @param {string} baseColor - The base color of the annotation (hex format)
 * @param {number} spacing - Spacing between stripes in pixels
 * @param {number} lineWidth - Width of the stripe lines in pixels
 * @param {number} angle - Angle of the stripes in degrees (45 for diagonal)
 * @returns {Object} - Konva pattern object
 */
export const createStripePattern = (baseColor, spacing = 16, lineWidth = 1, angle = 45) => {
  // Create a canvas element to draw the pattern
  const canvas = document.createElement('canvas');
  const size = spacing * 2; // Double the size for better pattern repetition
  canvas.width = size;
  canvas.height = size;
  
  const ctx = canvas.getContext('2d');
  
  // Fill with transparent background
  ctx.clearRect(0, 0, size, size);
  
  // Create seamless white diagonal stripes
  ctx.strokeStyle = '#FFFFFF'; // Pure white stripes
  ctx.lineWidth = lineWidth;
  ctx.globalAlpha = 0.7; // Semi-transparent for subtle effect
  
  // Draw multiple diagonal lines to ensure seamless tiling
  ctx.beginPath();
  
  // Draw lines that extend beyond pattern boundaries to eliminate gaps
  for (let i = -size; i <= size * 2; i += spacing) {
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size, size);
  }
  
  ctx.stroke();
  
  // Return the pattern in the format expected by Konva
  return canvas;
};

/**
 * Helper function to lighten a color
 * @param {string} color - Hex color code
 * @param {number} amount - Amount to lighten (0-1)
 * @returns {string} - Lightened hex color
 */
export const lightenColor = (color, amount) => {
  // Remove # if present
  color = color.replace('#', '');
  
  // Parse the hex values
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);
  
  // Lighten the color
  r = Math.min(255, Math.round(r + (255 - r) * amount));
  g = Math.min(255, Math.round(g + (255 - g) * amount));
  b = Math.min(255, Math.round(b + (255 - b) * amount));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Helper function to darken a color
 * @param {string} color - Hex color code
 * @param {number} amount - Amount to darken (0-1)
 * @returns {string} - Darkened hex color
 */
export const darkenColor = (color, amount) => {
  // Remove # if present
  color = color.replace('#', '');
  
  // Parse the hex values
  let r = parseInt(color.substring(0, 2), 16);
  let g = parseInt(color.substring(2, 4), 16);
  let b = parseInt(color.substring(4, 6), 16);
  
  // Darken the color
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};